import {
  ChevronDownIcon,
  ArrowTopRightOnSquareIcon,
  ArrowRightStartOnRectangleIcon
} from '@heroicons/react/24/outline';
import { default as Select } from 'antd/es/select';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import { usePostCustomerOrganisationsIdSetDefault } from 'api';

import { useLogoutMutation } from 'reduxLocal/api/authenticationApi';
import { logout } from 'reduxLocal/slices/authSlice';

import { useOrganisationId, useOrganisations } from 'hooks/context';

export const OrgDropDown = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const [logoutQuery] = useLogoutMutation();
  const setSearchParams = useSearchParams()[1];
  const openNotification = useOpenNotification();

  const handleLogout = async () => {
    await logoutQuery();
    setSearchParams('user_logout');
    dispatch(logout());
  };

  const organisationId = useOrganisationId();
  const organisations = useOrganisations();

  const organisationOptions = useMemo(() => {
    return (
      organisations.map((org) => {
        return {
          value: org.id,
          label: org.name,
          type: 'org',
          'data-testid': 'dashboard-organisations-dropdown-entry'
        };
      }) ?? []
    );
  }, [organisations]);

  const { mutateAsync: setDefaultOrg } =
    usePostCustomerOrganisationsIdSetDefault({
      mutation: {
        onError: () => {
          openNotification({
            kind: 'warning',
            message: 'Unable to set default organisation'
          });
        }
      }
    });

  const defaultOrganisationOption = useMemo(
    () =>
      organisationOptions.find((org) => org.value === Number(organisationId)),
    [organisationId, organisationOptions]
  );

  if (organisations.length < 1) {
    return null;
  }

  return (
    <Select
      className="w-64"
      key={defaultOrganisationOption?.value}
      options={organisationOptions}
      defaultValue={defaultOrganisationOption}
      showSearch
      filterOption={(input, option) =>
        (option?.label ?? '')
          .toLocaleLowerCase()
          .includes(input.toLocaleLowerCase())
      }
      onSelect={(value, option) => {
        navigate(`/org/${value}`);
        setDefaultOrg({ id: option.value });
      }}
      data-testid="dashboard-organisations-dropdown"
      listHeight={400}
      suffixIcon={<ChevronDownIcon width={20} height={20} />}
      popupRender={(menu) => (
        <>
          {menu}
          <div className="flex flex-col">
            <Link to={`${window.location.origin}/docs`} target="_blank">
              <div className="flex flex-row justify-between rounded-2xl p-3 hover:cursor-pointer hover:bg-surface-muted">
                API Reference
                <ArrowTopRightOnSquareIcon height={20} width={20} />
              </div>
            </Link>
            <div
              className="flex flex-row justify-between rounded-2xl p-3 hover:cursor-pointer hover:bg-surface-muted"
              onClick={handleLogout}
            >
              Log out
              <ArrowRightStartOnRectangleIcon height={20} width={20} />
            </div>
          </div>
        </>
      )}
    />
  );
};
